/**
 * Bill approval ribbon — `prototype/purchases/bill-detail.html` ▸ `.ribbon.amber`.
 *
 * Shown only when status is `approval_pending`. Markup uses `.ic` icon,
 * `.body` with checks line containing three `.c` items (PO match, GRN,
 * GSTIN/tax).
 */
import { Check, ShieldCheck } from "lucide-react";
import { paiseToInrShort } from "../../../lib/format.ts";
import type { BillStatus } from "../list/index.ts";

/** Status and checks used to decide whether bill approval needs a ribbon. */
export interface BillDetailRibbonProps {
  status: BillStatus;
  totalPaise: string;
  vendorGstin: string | null;
  threeWayMatch: "matched" | "mismatch" | "na";
}

/** Render the conditional founder-approval ribbon for bills. */
export function BillDetailRibbon({
  status,
  totalPaise,
  vendorGstin,
  threeWayMatch,
}: BillDetailRibbonProps) {
  if (status !== "approval_pending") return null;
  return (
    <section className="ribbon" role="status">
      <span className="ic" aria-hidden>
        <ShieldCheck size={14} />
      </span>
      <div className="body">
        <strong>Awaiting your approval</strong> — this bill is{" "}
        <span className="num">{paiseToInrShort(totalPaise)}</span>, above your founder threshold.
        The 3-way match has already passed; this is final sign-off.
        <div className="checks">
          <div className="c">
            <Check size={12} strokeWidth={2.6} />
            {threeWayMatch === "matched" ? "PO matches bill" : "PO needs review"}
          </div>
          <div className="c">
            <Check size={12} strokeWidth={2.6} />
            Goods received (GRN entity lands V1.x — assumed matched)
          </div>
          <div className="c">
            <Check size={12} strokeWidth={2.6} />
            {vendorGstin ? "GSTIN active · tax derived" : "GSTIN not on file"}
          </div>
        </div>
      </div>
    </section>
  );
}
